/**
 * * V1.3
 *
 * User: shlomif
 * Date: 19/1/13
 *
 * Refactor component:
 * - rearrange code in folders
 * - add ToggleButtonModel
 * - add lock permutation
 *
 * Updated API:
 *
 * options: {
 *   container: myContainerElement, // mandatory
 *   enabled: "true",
 *   checked: "true"
 *   tooltip: {
 *      active: "active tooltip text",
 *      inactive: "inactive tooltip text"
 *      },
 *   permutation: ToggleButtonView.PERMUTATION.CHECK,
 *   transition: ToggleButtonView.TRANSITION.FAST, // animation not yet implemented
 *   commitStateChange: true // if true - clicking changes the internal state of the button. If false - an event
 *                           // is triggered. See ToggleButtonView.TRIGGERING.CHANGE.
 *
 *  public methods:
 *
 *  + toggle
 *  + isEnabled
 *  + setEnabled
 *  + isChecked
 *  + setChecked
 *
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * V1.2
 *
 * User: shaulm
 * Date: 10/7/13

 * This widget is used to implement an toggle button component on the page.
 * Currently we support 2 transition modes: slow (400ms) and fast(200ms).
 * Options:
 * container - The element on the page where the component will be created.
 * checked - checked or unchecked.
 * transition - fast or slow
 * commitStateChange - determine if should change UI & State by himself or externally - true by default
 *
 * Using Example:
 *---------------------------
 * template:
 *<div class="activeArea"></div>
 *
 *  js:
 * var toggleBtn = new ToggleButton({
 *   container:this.ui.activeArea,
 *   transition: ToggleButton.TRANSITION.FAST,
 *   checked: true,
 *   commitStateChange: true // true by default
 *   tooltip: {active: '',inactive: ''}
 * });
 *  toggleBtn.render();
 *
 * *******************************************************************************************************
 */

define(function (require) {
    "use strict";

    var Marionette = require("marionette");
    var template = require("ui/toggle-button/templates/toggleButton.tmpl");
    var _ = require("underscore");

    var ToggleButtonModel = require("ui/toggle-button/js/models/ToggleButtonModel");

    var ToggleButtonView = Marionette.ItemView.extend({
        template: template,
        className: "toggleButton",

        ui: {
            elm: ".btn-toggle"
        },

        events: {
            "click .btn-toggle": "onButtonClicked"
        },

        modelEvents: {
            "change": "_onModelChanged"
        },

        //--------------------------------------------------------------------------------------------------------------

        initialize: function (options) {
            this.container = options.container;

            // Mutable options (can be modified later)
            this.model = new ToggleButtonModel({
                enabled: _.has(options, "enabled") ? options.enabled : true,
                checked: _.has(options, "checked") ? options.checked : true
            });

            if(_.has(options, "tooltip")) {
                this.model.set("checkedTooltip", _.has(options.tooltip, "active") ? options.tooltip.active : "");
                this.model.set("notCheckedTooltip", _.has(options.tooltip, "inactive") ? options.tooltip.inactive : "");
                this.model.set("tooltipAlign", _.has(options.tooltip, "tooltipAlign") ? options.tooltip.tooltipAlign : ToggleButtonView.TOOLTIP_ALIGN.MIDDLE);
            }

            // Immutable options (can't be modified after initialization)
            this.permutation = options.permutation || ToggleButtonView.PERMUTATION.CHECK;
            this.transition = options.transition || ToggleButtonView.TRANSITION.FAST;
            this.commitStateChange = _.has(options, "commitStateChange") ? options.commitStateChange : true;
        },

        //--------------------------------------------------------------------------------------------------------------

        onRender: function () {
            this.ui.elm.addClass("permutation-" + this.permutation);
            this.ui.elm.addClass("TRAN-" + this.transition);

            if(this.container) {
                this.container.append(this.$el);
            }

            this._updateTooltipText();
        },

        //--------------------------------------------------------------------------------------------------------------

        onButtonClicked: function (e) {
            var that = this;

            e.preventDefault();

            if (this.isEnabled()) {
                if (this.commitStateChange) { // change state internally and notify
                    this.toggle();
                    setTimeout(function(){
                        that.trigger(ToggleButtonView.TRIGGERING.CHANGE, that.isChecked());
                    },200);
                } else { // notify state change but do not commit it
                    var pendingState  = !this.isChecked();
                    that.trigger(ToggleButtonView.TRIGGERING.CHANGE, pendingState);
                }
            }
        },

        //--------------------------------------------------------------------------------------------------------------

        toggle: function () {
            this.setChecked(!this.isChecked());
        },

        //--------------------------------------------------------------------------------------------------------------

        isEnabled: function() {
            return this.model.get("enabled");
        },

        //--------------------------------------------------------------------------------------------------------------

        setEnabled: function(enabled) {
            this.model.set("enabled", enabled);
        },

        //--------------------------------------------------------------------------------------------------------------

        isChecked: function() {
            return this.model.get("checked");
        },

        //--------------------------------------------------------------------------------------------------------------

        setChecked: function(checked) {
            this.model.set("checked", checked);
        },

        //--------------------------------------------------------------------------------------------------------------

        _onModelChanged: function() {
            this.render();
            this._updateTooltipText();
        },

        //--------------------------------------------------------------------------------------------------------------

        _updateTooltipText: function () {
            var title = this.isChecked() ? this.model.get("checkedTooltip") : this.model.get("notCheckedTooltip");
            this.ui.elm.attr("title", title );
            this.ui.elm.attr("tooltip-text", title );   // Update the tooltip directly to handle the case in which the
                                                        // tooltip is opened during a state change
        }
    });

    ToggleButtonView.TRANSITION = {
        FAST :1,
        SLOW :2
    };

    ToggleButtonView.PERMUTATION = {
        CHECK: "check",
        LOCK: "lock",
        NIGHT_THEME: "night-theme"
    };

    ToggleButtonView.TRIGGERING = {
        CHANGE: "toggleButton:change" // @param isChecked boolean
    };

    ToggleButtonView.TOOLTIP_ALIGN = {
        MIDDLE: "middle",
        LEFT: "left",
        RIGHT: "right",
        TOP: "top",
        BOTTOM: "bottom"
    };

    return ToggleButtonView;
});
